import Padding from '@/components/padding/Padding';
import usePrefersDark from '@/hooks/usePrefersDark';
import { Box, Button, Heading, Text } from '@chakra-ui/react';
import React, { useState } from 'react';
import { FaCheck, FaChevronRight } from 'react-icons/fa';

type PricingCardProps = {
  bgClass: string;
  headingColor: string;
  title: string;
  description: string;
  price: string;
  unitLines: string[];
  textColor?: string;
  footnote?: string;
  features?: string[];
  featuresTitle?: string;
};

const PricingCard = ({
  bgClass,
  headingColor,
  title,
  description,
  price,
  unitLines,
  textColor,
  footnote,
  features,
  featuresTitle,
}: PricingCardProps): React.JSX.Element => {
  return (
    <Box
      className={`${bgClass} p-8 w-full md:w-[360px] h-full flex flex-col ${bgClass.includes('rounded-none') ? '' : 'rounded-lg shadow-md'}`}
    >
      <Heading color={headingColor} fontSize="2xl" style={{ fontFamily: 'Bebas Neue' }}>
        {title}
      </Heading>
      {/* Description grows to align price and features separator */}
      <Box mt={4} className={features ? 'md:flex-1' : ''}>
        <Text fontSize="sm" color={textColor} style={{ fontFamily: 'Lato' }}>
          {description}
        </Text>
      </Box>
      <Box mt={4} className="flex flex-row gap-2 items-center">
        <Text fontSize="3xl" fontWeight="bold" color={textColor} style={{ fontFamily: 'Lato' }}>
          {price}
        </Text>
        <Box>
          {unitLines.map((line, index) => (
            <Text
              key={`${title}-unit-${index}`}
              fontStyle="italic"
              fontSize="xs"
              lineHeight={1}
              color={textColor}
              style={{ fontFamily: 'Lato' }}
            >
              {line}
            </Text>
          ))}
        </Box>
      </Box>
      {/* Button or footnote */}
      {footnote ? (
        <Text mt={6} fontSize="xs" color={textColor} opacity={0.7} style={{ fontFamily: 'Lato' }}>
          *{footnote}
        </Text>
      ) : (
        <Button
          mt={6}
          onClick={() =>
            (window.location.href = '/learn-more/seamless-payments-refunds#price-example')
          }
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="full"
          variant="outline"
          borderColor="currentColor"
        >
          Example calculations
          <FaChevronRight />
        </Button>
      )}
      {/* Features list - desktop only */}
      {features && features.length > 0 && (
        <Box className="hidden lg:flex lg:flex-col lg:flex-1 mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-600">
          {featuresTitle && (
            <Text
              fontSize="sm"
              fontWeight="bold"
              color={headingColor}
              mb={3}
              style={{ fontFamily: 'Lato' }}
            >
              {featuresTitle}
            </Text>
          )}
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <FaCheck className="mt-1 flex-shrink-0" style={{ color: headingColor }} size={12} />
                <Text fontSize="sm" color={textColor} style={{ fontFamily: 'Lato' }}>
                  {feature}
                </Text>
              </li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
};

type BillingCycle = 'annual' | 'monthly';

const CycleToggle = ({
  cycle,
  onChange,
}: {
  cycle: BillingCycle;
  onChange: (cycle: BillingCycle) => void;
}): React.JSX.Element => (
  <Box className="flex justify-center mt-8 mb-10">
    <Box className="inline-flex items-center bg-white/10 backdrop-blur-md border border-neutral-300 dark:border-white/20 rounded-xl p-1 shadow-lg">
      {(['annual', 'monthly'] as const).map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={`flex flex-col items-center px-6 py-2 rounded-xl transition-all duration-200 cursor-pointer ${
            cycle === option
              ? 'bg-[#c12c8e] text-white'
              : 'text-neutral-700 dark:text-neutral-200 hover:bg-white/10'
          }`}
        >
          <span
            className="text-base font-bold leading-tight"
            style={{ fontFamily: 'Lato' }}
          >
            {option === 'annual' ? 'Annual' : 'Monthly'}
          </span>
          <span
            className="text-[10px] leading-tight opacity-70"
            style={{ fontFamily: 'Lato' }}
          >
            membership cycle
          </span>
        </button>
      ))}
    </Box>
  </Box>
);

const Pricing = (): React.JSX.Element => {
  const prefersDark = usePrefersDark();
  const [cycle, setCycle] = useState<BillingCycle>('annual');
  const isAnnual = cycle === 'annual';

  const membershipPrice = isAnnual ? '£4' : '33p';
  const membershipUnitLines = isAnnual
    ? ['per membership', 'payment']
    : ['per membership', 'payment'];
  const membershipDescription = isAnnual
    ? "The membership fee is £4 per membership payment, which is paid within the club website. This fee is automatically collected from each member's renewal and joining payments. No invoices, no chasing payments, no counting members - it's all taken care of."
    : "The membership fee is 33p per membership payment, which is paid within the club website. This fee is automatically collected from each member's monthly payments. No invoices, no chasing payments, no counting members - it's all taken care of.";

  return (
    <Padding verticalPadding={20} backgroundColor={prefersDark ? undefined : '#16161f09'}>
      <Heading fontSize="4xl" style={{ fontFamily: 'Bebas Neue' }}>
        Pricing
      </Heading>
      <Text fontSize="lg" style={{ fontFamily: 'Lato' }} className="mt-2 lg:w-[70%]">
        As a club - Olympiq is <b>free to use</b>. However we do charge a small application fee on
        every payment processed (membership renewals, sign-ups, and session bookings). The fee is
        deducted from the payment, so there are no invoices to manage. It is 10p per paid session
        booking (no charge for free sessions) and {isAnnual ? '£4 for annual' : '33p for monthly'}{' '}
        membership payments. Stripe also charges a small processing fee, lets dive into some
        examples;
      </Text>
      <CycleToggle cycle={cycle} onChange={setCycle} />
      <Box className="flex flex-col lg:flex-row gap-8 mt-4 justify-center items-start">
        {/* Conjoined fee cards */}
        <Box className="flex flex-col md:flex-row md:items-stretch rounded-xl overflow-hidden shadow-lg">
          <PricingCard
            bgClass="bg-[#fdf3fb] dark:bg-neutral-800 rounded-none"
            headingColor={prefersDark ? '#ea7ec8' : '#c12c8e'}
            title="Membership fee"
            description={membershipDescription}
            price={membershipPrice}
            unitLines={membershipUnitLines}
            featuresTitle="Website features"
            features={[
              'Apple Pay & Google Pay',
              'Automated renewal reminders',
              'Auto-removal of expired members from website and app',
              'Automated custom welcome and goodbye emails',
              'Award year membership to volunteers',
              'Create club blogs',
              'List club championship races and live leaderboard',
              'Share sessions with partner clubs',
              'And so much more...',
            ]}
          />
          <Box className="hidden md:block w-px self-stretch bg-neutral-200 dark:bg-neutral-700" />
          <Box className="md:hidden h-px bg-neutral-200 dark:bg-neutral-700" />
          <PricingCard
            bgClass="bg-[#fdf7ee] dark:bg-neutral-800 rounded-none"
            headingColor={prefersDark ? '#ea7ec8' : '#f38225'}
            title="Session booking fee"
            description="The Olympiq mobile app is free to use, but a 10p fee is charged on every booking transaction - price matched to Spond. This fee is automatically taken from each member payment, so there's no invoicing or extra admin. No fee exists for free sessions."
            price="10p"
            unitLines={['per', 'transaction']}
            featuresTitle="Mobile app features"
            features={[
              'Apple Pay & Google Pay',
              'Refunds on cancellation',
              'Free sessions at no charge',
              'Trial periods',
              'Link family accounts',
              'Enable free sessions for privelaged members',
              'Same login as club website',
              'Club posts and polls',
              'Emergency contact details',
              'Club championship tracker',
              'View session attendee list',
              'And so much more...',
            ]}
          />
        </Box>
        {/* Referral bonus - separate, sticky on desktop */}
        <Box className="lg:sticky lg:top-24 self-start">
          <PricingCard
            bgClass="bg-neutral-800 dark:bg-neutral-800"
            headingColor="white"
            textColor="white"
            title="Referal bonus"
            description="Receive £250 paid directly to your club or personal bank account when you refer a new club of at least 100 members. The bonus is paid once the new club has fully onboarded to Olympiq."
            price="£250"
            unitLines={['per', 'referred club']}
            footnote="Referred club must have a minimum of 100 active members and complete full onboarding."
          />
        </Box>
      </Box>
    </Padding>
  );
};

export default Pricing;
